import Stack from '@mui/material/Stack';
import { colors } from '@sweep-io/sweep-design';
import { RelatedListTable } from '../configuration-item-tables/RelatedListTable';
import { humanizeDate } from '../../helpers/humanizeDate';
import { SalesforceItem } from '../../parser/ParserTypes';
import { TruncatedTextTooltip } from '../../common/TruncatedTextTooltip';
import { RECORD_TYPE, LAYOUT } from '@server/parser';
import { DetailsSectionShell } from '../building-blocks/documentation-card/details/DetailsSectionShell';
import { DetailsItem } from '../building-blocks/documentation-card/details/DetailsItem';
import { DetailsWrapperWithHeader } from '../building-blocks/documentation-card/details/DetailsWrapperWithHeader';

const DETAILS_TEXT_COLOR = colors.grey[700];

interface DetailsSectionProps {
  configurationItem: SalesforceItem;
  isField?: boolean;
  isExpandedByDefault?: boolean;
}

export const DetailsSection = ({
  isField,
  configurationItem,
  isExpandedByDefault,
}: DetailsSectionProps) => {
  const { description, helpText, name, createdAt } = configurationItem;

  const flowDescription =
    'flowDescription' in configurationItem ? configurationItem.flowDescription : undefined;
  const formula = 'formula' in configurationItem ? configurationItem.formula : undefined;
  const isMandatory =
    'isMandatory' in configurationItem ? configurationItem.isMandatory : undefined;
  const buttons = 'buttons' in configurationItem ? configurationItem.buttons : undefined;
  const relatedLists =
    'relatedLists' in configurationItem ? configurationItem.relatedLists : undefined;

  const isRecordType = configurationItem.parentType === RECORD_TYPE;
  const isEmptyField = !helpText && !name && !description && !createdAt;
  const isEmptyRecordType = isRecordType && !name && !description && !createdAt;

  const isLayout = configurationItem.parentType === LAYOUT;
  const isEmptyLayout = !buttons?.length && !relatedLists?.length && !createdAt;

  const isEmptyConfiguration = !flowDescription && !description && !formula && !createdAt;

  const isEmpty = isField
    ? isEmptyField
    : isRecordType
      ? isEmptyRecordType
      : isLayout
        ? isEmptyLayout
        : isEmptyConfiguration;

  const shouldDisplayApiName = name && (isField || isRecordType);

  return (
    <DetailsSectionShell
      title="Details"
      sectionKey="details"
      isExpandedByDefault={isExpandedByDefault}
      isEmpty={isEmpty}
    >
      <Stack pt="12px" gap="12px" width="100%" minWidth={0}>
        {description && <DetailsItem headerTxt="Description" contentTxt={description} />}
        <Stack gap="12px" display="grid" gridTemplateColumns="1fr 1fr" width="100%">
          {shouldDisplayApiName && (
            <DetailsItem headerTxt="API Name" contentTxt={name} isMandatory={isMandatory} />
          )}
          {createdAt && (
            <DetailsItem
              headerTxt="Creation date"
              contentTxt={humanizeDate({ dateOrTimestamp: createdAt })}
            />
          )}
          {helpText && isField && <DetailsItem headerTxt="Help Text" contentTxt={helpText} />}
          {formula && (
            <DetailsWrapperWithHeader headerTxt="Formula">
              <TruncatedTextTooltip title={formula} variant="caption" color={DETAILS_TEXT_COLOR}>
                {formula}
              </TruncatedTextTooltip>
            </DetailsWrapperWithHeader>
          )}
          {flowDescription && (
            <DetailsItem headerTxt="Flow description" contentTxt={flowDescription} />
          )}
          {isLayout && (
            <>
              {!!buttons?.length && (
                <DetailsItem
                  headerTxt="Buttons"
                  contentTxt={buttons.map((button: { label: string }) => button.label).join(', ')}
                />
              )}
            </>
          )}
        </Stack>
        {!!relatedLists?.length && (
          <RelatedListTable tableHeader="Related list" items={relatedLists} section="details" />
        )}
      </Stack>
    </DetailsSectionShell>
  );
};
